import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent, SelectProps } from "@mui/material/Select";
import { get } from "radash";
import { Controller, UseControllerProps } from "react-hook-form";

import { LoadingEndAdornment } from "./DynamicAutocomplete";

export type SelectInputOptionType = {
  label: string;
  value: any;
  disabled?: boolean;
  [x: string]: any;
};

export type SelectInputProps = Omit<SelectProps, "variant" | "onChange"> &
  UseControllerProps<any> & {
    label: string;
    loading?: boolean;
    required?: boolean;
    fallback?: string;
    children?: React.ReactNode;
    options: SelectInputOptionType[] | string[] | any[];
    getOptionKey?: (option: SelectInputOptionType | string) => string;
    getSelectedValue?: (value: any) => string;
    onValueChange?: (e: SelectChangeEvent<any>) => void;
  };

const SelectInput = (props: SelectInputProps) => {
  const {
    control,
    name,
    size,
    options,
    children,
    loading = false,
    required,
    getOptionKey,
    fallback = "No options",
    onValueChange,
    getSelectedValue,
    label,
    ...rest
  } = props;

  const renderPlaceHolder = (value: any): any => {
    return value === undefined || value === ""
      ? () => (
          <div style={{ color: "#aaa" }}>
            {props.placeholder || props.label || ""}
          </div>
        )
      : undefined;
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { onChange, value: fieldValue, ...fieldRest },
        formState: state,
      }) => {
        const value = getSelectedValue
          ? getSelectedValue(fieldValue)
          : fieldValue;

        return (
          <FormControl
            fullWidth
            size={size}
            required={required}
            error={!!get(state.errors, name) || rest.error}
            sx={{
              ".MuiFormLabel-root": {
                "&.Mui-focused": {
                  top: 0,
                },
              },
            }}
          >
            <InputLabel>{label}</InputLabel>
            <Select
              // notched
              // displayEmpty
              label={label as string}
              MenuProps={{ sx: { maxHeight: 350 } }}
              endAdornment={<LoadingEndAdornment loading={loading} />}
              value={getSelectedValue ? getSelectedValue(value) : value}
              onChange={(e) => {
                onChange(e);
                onValueChange?.(e);
              }}
              {...fieldRest}
              {...rest}
              // renderValue must be below to always render the placeholder
              renderValue={renderPlaceHolder(value)}
            >
              {!options.length && (
                <MenuItem value="" disabled sx={{ fontSize: 14 }}>
                  {fallback}
                </MenuItem>
              )}

              {options.map((option, i) => {
                const label: any = get(option, "label", option);
                const value: any = get(option, "value", option);

                return (
                  <MenuItem
                    key={label + i}
                    value={value}
                    disabled={option.disabled}
                    sx={{ fontSize: 14 }}
                  >
                    {label}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        );
      }}
    />
  );
};

export default SelectInput;
